import { CountryCode } from "@vygruppen/vy-train-map";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { trainRouteStopToStopId } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/AffectedStopsFields";
import {
  SearchDirection,
  toNonExcludedStopId,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/listofExcludedStations";
import { RouteSection } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import {
  TrainRoute,
  TrainRouteStop,
} from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { FC } from "react";
import colors from "shared/theme/colors";

/**
 * Helper functions for sorting trainRoute stops into (probable) passed and notPassed stops,
 * then displaying them as grouped options in the choose stop dropdown.
 *
 * Based on: stop.isArrived
 *
 * Best guess: Split on last index of stop which isArrived
 * */

export const AffectedStopOption: FC<{ stop: TrainRouteStop }> = ({
  stop: { stopId, name },
}) => <option value={stopId}>{name}</option>;

function buildOptGroups(passed: TrainRouteStop[], notPassed: TrainRouteStop[]) {
  const passedOpts = passed.map((stop) => (
    <AffectedStopOption key={stop.stopId} stop={stop} />
  ));
  const notPassedOpts = notPassed.map((stop) => (
    <AffectedStopOption key={stop.stopId} stop={stop} />
  ));
  return (
    <>
      <optgroup label="Passerte" color={colors.steel}>
        {passedOpts}
      </optgroup>
      <optgroup label="Kommende" color={colors.steel}>
        {notPassedOpts}
      </optgroup>
    </>
  );
}

export function splitPassedStopsAndBuildOptGroups(stops?: TrainRouteStop[]) {
  // Early out if trainRoute stops is undefined or empty
  if (stops === undefined || stops.length === 0) {
    return buildOptGroups([], []);
  }

  // Because our data is spotty we find index of last stop with isArrived and split there
  const lastPassedIndex = stops.findLastIndex((stop) => stop.isArrived);

  // if lastPassedIndex is -1 we did not find any isArrived, so all should be notPassed
  if (lastPassedIndex === -1) {
    return buildOptGroups([], stops);
  }

  const passed = stops.slice(0, lastPassedIndex + 1);
  const notPassed = stops.slice(lastPassedIndex + 1);

  return buildOptGroups(passed, notPassed);
}

/**
 *
 * @param stop
 * @param fromStop
 *
 * @returns List of <AffectedStopOption /> elements with stops after fromStop
 */
export const getToStopOptions = (stops: TrainRouteStop[], fromStop: string) =>
  stops
    .slice(stops.findIndex((s) => s.stopId === fromStop) + 1)
    .map((stop) => <AffectedStopOption key={stop.stopId} stop={stop} />);

export type StopPredicate = "firstStop" | "lastStop" | "fromStop" | "toStop";
export const routeSectionMethods = (
  stops: TrainRouteStop[],
  selectableStops: TrainRouteStop[],
  countryCode: CountryCode,
) => {
  // Not necessary, but helps a bit with readability
  const findStop = (id: string | null | undefined) =>
    stops.find((it) => it.stopId === id);

  const toRouteSection = (
    fromStop: TrainRouteStop,
    toStop: TrainRouteStop,
  ): RouteSection => {
    const displayedFromStopId = toNonExcludedStopId(
      stops,
      fromStop.stopId,
      SearchDirection.SearchBackward,
    );
    const displayedFromStop = findStop(displayedFromStopId)!;
    const displayedToStopId = toNonExcludedStopId(
      stops,
      toStop.stopId,
      SearchDirection.SearchForward,
    );
    const displayedToStop = findStop(displayedToStopId)!;
    const getRouteSectionType = () => {
      const fromIndex = selectableStops.indexOf(displayedFromStop);
      const toIndex = Math.max(
        fromIndex,
        selectableStops.indexOf(displayedToStop),
      );
      if (fromIndex === toIndex) return "AT_STOP";
      if (fromIndex === 0 && toIndex === selectableStops.length - 1)
        return "WHOLE_ROUTE";
      return "BETWEEN_STOPS";
    };
    const routeSectionType = getRouteSectionType();
    switch (routeSectionType) {
      case "AT_STOP":
        return {
          type: "AT_STOP",
          fromStop: trainRouteStopToStopId(displayedFromStop, countryCode),
        };
      case "BETWEEN_STOPS":
        return {
          type: "BETWEEN_STOPS",
          fromStop: trainRouteStopToStopId(displayedFromStop, countryCode),
          toStop: trainRouteStopToStopId(displayedToStop, countryCode),
        };
      case "WHOLE_ROUTE":
        return {
          type: "WHOLE_ROUTE",
          fromStop: trainRouteStopToStopId(displayedFromStop, countryCode),
          toStop: trainRouteStopToStopId(displayedToStop, countryCode),
        };
    }
  };

  const autofillInfrastructure = (
    infrastructureEvent: InfrastructureResponse,
  ): RouteSection | undefined => {
    const affectedLegs = infrastructureEvent.affectedLegs.flatMap((leg) => [
      leg.fromStopId.id,
      leg.toStopId.id,
    ]);
    const firstStopIndex = stops.findIndex(({ stopId }) =>
      affectedLegs.includes(stopId),
    );
    const lastStopIndex = stops.findLastIndex(({ stopId }) =>
      affectedLegs.includes(stopId),
    );

    if (firstStopIndex === -1 || lastStopIndex === -1) {
      // The route is not affected by this event, cannot autofill from event
      return undefined;
    }
    return toRouteSection(stops[firstStopIndex], stops[lastStopIndex]);
  };

  const autofillTrainRoute = (
    trainRoute: TrainRoute | undefined,
    fromStopPredicate: StopPredicate,
    toStopPredicate?: StopPredicate,
  ): RouteSection => {
    const firstStop = stops[0];
    const lastStop = stops[stops.length - 1];

    // Check if the train is currently somewhere along its route
    const fromStopId = trainRoute?.currentTrainRouteSection?.fromStopId;
    const fromStop = findStop(fromStopId) ?? firstStop; // Fallback to firstStop
    const toStopId = trainRoute?.currentTrainRouteSection?.toStopId;
    const toStop = findStop(toStopId);

    const stopByPredicate = (stopPredicate: StopPredicate) => {
      switch (stopPredicate) {
        case "firstStop":
          return firstStop;
        case "lastStop":
          return lastStop;
        case "fromStop":
          return fromStop;
        case "toStop":
          return toStop ?? fromStop; // since toStop is optional, we want it to fallback to fromStop
      }
    };
    return toRouteSection(
      stopByPredicate(fromStopPredicate),
      stopByPredicate(toStopPredicate ?? fromStopPredicate), // Use the same stop for AT_STOP
    );
  };
  return {
    findStop,
    toRouteSection,
    autofillInfrastructure,
    autofillTrainRoute,
  } as const;
};
